'use client'

import React from 'react'
import { Swiper as SwiperCore, SwiperOptions } from 'swiper'
import {
  Swiper as ReactSwiper,
  SwiperSlide as ReactSwiperSlide,
} from 'swiper/react'

import { initSwiper } from './internal/initSwiper'
import { Nav } from './internal/Nav'
import { Container, Controls } from './styles'

initSwiper()

export interface RenderControlsArgs {
  navigation: SwiperOptions['navigation']
  prevClass: string
  nextClass: string
  button?: React.ReactNode
  prevArrow?: React.ReactNode
  nextArrow?: React.ReactNode
  hideControlsOnMobile?: boolean
}

export type RenderControls = (options: RenderControlsArgs) => React.ReactNode

export interface SwiperProps {
  /**
   * ID of the swiper
   */
  id: string

  /**
   * Array of slides
   */
  slides: React.ReactNode[]

  /**
   * Space between slides
   */
  spaceBetween?: number

  /**
   * Visible slides per view
   */
  slidesPerView?: number

  /**
   * Pagination config
   */
  pagination?: SwiperOptions['pagination']

  /**
   * Cover pagination on top of slides
   */
  coverPagination?: boolean

  /**
   * Virtualization config
   */
  virtual?: SwiperOptions['virtual']

  /**
   * Breakpoint config
   */
  breakpoints?: SwiperOptions['breakpoints']

  /**
   * Navigation (Only displayed on wide devices)
   */
  navigation?: boolean

  /**
   * Navigation next className
   */
  nextClass?: string

  /**
   * Navigation prev className
   */
  prevClass?: string

  /**
   * Hide navigation on click
   */
  hideNavigationOnClick?: boolean

  /**
   * Previous arrow element
   */
  prevArrow?: React.ReactNode

  /**
   * Next arrow element
   */
  nextArrow?: React.ReactNode

  /**
   * Loop slides
   */
  loop?: boolean

  /**
   * Triggered when slide changes
   */
  onSlideChange?: (swiper: SwiperCore) => void

  /**
   * Button shown between the navigations.
   * If button is provided, `pagination` prop will be disabled automatically.
   */
  button?: React.ReactNode

  /**
   * Render navigation
   */
  renderControls?: RenderControls

  /**
   * Hide controls on mobile phones
   */
  hideControlsOnMobile?: boolean
}

const paginationOptions = { clickable: true }

const noop = () => {
  // NOOP
}

const defaultRenderControls: RenderControls = ({
  navigation,
  button,
  prevClass,
  nextClass,
  prevArrow,
  nextArrow,
  hideControlsOnMobile,
}) => (
  <Controls
    next={nextClass}
    prev={prevClass}
    hideControlsOnMobile={hideControlsOnMobile}
  >
    {navigation && <div className={prevClass}>{prevArrow}</div>}

    {button && (
      // eslint-disable-next-line tailwindcss/no-custom-classname
      <div className="buttonWrapper">{button}</div>
    )}

    {navigation && <div className={nextClass}>{nextArrow}</div>}
  </Controls>
)

/**
 * Wrapper of [swiper](https://swiperjs.com/) with some preset and restrictions
 */
export const Swiper = ({
  id,
  slides,
  spaceBetween = 0,
  slidesPerView = 1,
  pagination = false,
  coverPagination = false,
  navigation = false,
  prevArrow = <Nav flip />,
  nextArrow = <Nav />,
  nextClass = `swiper_next`,
  prevClass = `swiper_prev`,
  hideNavigationOnClick = false,
  breakpoints,
  virtual = true,
  loop = false,
  onSlideChange = noop,
  button,
  renderControls = defaultRenderControls,
  hideControlsOnMobile = false,
}: SwiperProps) => {
  const hasControls =
    navigation || button || renderControls !== defaultRenderControls

  const uniquePrevClass = `${id}_${prevClass}`
  const uniqueNextClass = `${id}_${nextClass}`

  const navigationOptions = React.useMemo<SwiperOptions['navigation']>(
    () => ({
      prevEl: `.${uniquePrevClass}`,
      nextEl: `.${uniqueNextClass}`,
      hideOnClick: hideNavigationOnClick,
    }),
    [uniquePrevClass, uniqueNextClass, hideNavigationOnClick],
  )

  return (
    <Container
      modifier={id}
      padded={!!pagination && !hasControls && !coverPagination}
    >
      <ReactSwiper
        wrapperClass="swiper-wrapper"
        containerModifierClass={`${id}-`}
        threshold={5}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        navigation={navigationOptions}
        pagination={
          button
            ? // Always disable pagination when a button is present
              false
            : pagination === true
            ? paginationOptions
            : pagination
        }
        onSlideChange={onSlideChange}
        virtual={virtual}
        loop={loop}
        breakpoints={breakpoints}
      >
        {slides.map((slide, i) => (
          <ReactSwiperSlide key={i} virtualIndex={virtual ? i : undefined}>
            {slide}
          </ReactSwiperSlide>
        ))}
      </ReactSwiper>

      {hasControls &&
        renderControls({
          navigation,
          prevClass: uniquePrevClass,
          nextClass: uniqueNextClass,
          button,
          nextArrow,
          prevArrow,
          hideControlsOnMobile,
        })}
    </Container>
  )
}
